import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, AlertTriangle, Clock, ChevronRight, Trash2, Info, Calendar, ArrowRight, RefreshCw } from 'lucide-react';
import { userStorage } from '../../utils/userStorage';
const SCHICHT_TYPES_D = ['Früh', 'Spät', 'Nacht', 'Frei'];
const DAY_NAMES = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const MONTHS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];
const MAX_SHOWN = 6;

function fmtDate(dateStr) {
  try {
    const d = new Date(dateStr + 'T12:00:00');
    return `${DAY_NAMES[d.getDay()]}. ${d.getDate()}. ${MONTHS[d.getMonth()]}`;
  } catch { return dateStr || ''; }
}

function t2m(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function m2t(m) {
  return `${String(Math.floor(m / 60)).padStart(2,'0')}:${String(m % 60).padStart(2,'0')}`;
}

export function collectConflictGroups(frequency, daysOfWeek, schichtZeiten, habitTime, habitDuration, appointments, skippedDates = [], dailyTimeOverrides = {}, excludeHabitId = null, schichtRhythmus = null) {
  // Heutiges Datum in lokaler Zeit (z.B. Deutschland/CET) – kein UTC-Vergleich
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  // Rhythmus-Start für Schicht-Filterung
  const rhythmusStart = (schichtRhythmus?.startDate)
    ? new Date(schichtRhythmus.startDate + 'T12:00:00').getTime()
    : null;

  const raw = [];
  for (const apt of appointments) {
    // Nur die eigenen Habit-Blöcke ausschließen – andere Habits' Kalenderblöcke SOLLEN als Konflikt erkannt werden
    // excludeHabitId = null/undefined (neues Habit) → alle Habit-Blöcke einschließen
    // excludeHabitId = habit.id (bestehendes Habit) → nur eigene Blöcke ausschließen
    if (apt.isHabitReminder && excludeHabitId != null && apt.habitId === excludeHabitId) continue;
    if (!apt.startTime || !apt.endTime) continue;
    if (apt.endDate && apt.date && apt.endDate !== apt.date) continue;
    // Vergangene Termine ignorieren (lokale Uhrzeit entscheidend)
    if (apt.date && apt.date < todayStr) continue;
    const as = t2m(apt.startTime), ae = t2m(apt.endTime);

    if (frequency === 'schichten') {
      // Rhythmus-Filterung: nur Tage prüfen an denen der jeweilige Schichttyp aktiv ist
      let schichtForDay = null;
      if (rhythmusStart !== null && schichtRhythmus?.cycleLength && apt.date) {
        const aptNoon = new Date(apt.date + 'T12:00:00').getTime();
        const offset = Math.round((aptNoon - rhythmusStart) / 86400000);
        if (offset < 0) continue; // Vor Rhythmus-Start
        const pos = ((offset % schichtRhythmus.cycleLength) + schichtRhythmus.cycleLength) % schichtRhythmus.cycleLength;
        schichtForDay = schichtRhythmus.days[pos] ?? 'Frei';
      }
      for (const s of SCHICHT_TYPES_D) {
        // Wenn Rhythmus bekannt: nur Termine an Tagen dieses Schichttyps prüfen
        if (schichtForDay !== null && schichtForDay !== s) continue;
        const z = schichtZeiten?.[s];
        if (!z?.calendarBlock || !z.time) continue;
        const hs = t2m(z.time), he = hs + (z.duration || 30);
        if (hs < ae && as < he) raw.push({ appointment: apt, shiftType: s, habitTime: z.time, habitDuration: z.duration || 30 });
      }
    } else {
      if (!habitTime) continue;
      // Bereits übersprungene Tage ignorieren
      if (apt.date && skippedDates.includes(apt.date)) continue;
      // Tagesweise Uhrzeit-Überschreibung berücksichtigen
      const effectiveTime = (apt.date && dailyTimeOverrides[apt.date]) ? dailyTimeOverrides[apt.date] : habitTime;
      const hs = t2m(effectiveTime), he = hs + (habitDuration || 15);
      // Kein Konflikt mehr wenn Überschreibung greift
      if (hs >= ae || as >= he) continue;
      if (frequency === 'custom' && daysOfWeek?.length > 0) {
        try {
          const d = new Date((apt.date || '') + 'T12:00:00');
          if (isNaN(d.getTime()) || !daysOfWeek.includes(d.getDay())) continue;
        } catch { continue; }
      }
      raw.push({ appointment: apt, shiftType: null, habitTime: effectiveTime, habitDuration: habitDuration || 15 });
    }
  }

  const groupMap = {};
  for (const c of raw) {
    const key = `${c.appointment.title || ''}||${c.appointment.startTime}||${c.shiftType || ''}`;
    if (!groupMap[key]) {
      groupMap[key] = { key, title: c.appointment.title || 'Termin', origStartTime: c.appointment.startTime, origEndTime: c.appointment.endTime, shiftType: c.shiftType, habitTime: c.habitTime, habitDuration: c.habitDuration, occurrences: [] };
    }
    groupMap[key].occurrences.push({ ...c.appointment });
  }
  // Innerhalb jeder Gruppe nach Datum sortieren, dann Gruppen nach frühstem Datum sortieren
  return Object.values(groupMap)
    .map(g => ({
      ...g,
      occurrences: g.occurrences.sort((a, b) => (a.date || '').localeCompare(b.date || '')),
    }))
    .sort((a, b) => (a.occurrences[0]?.date || '').localeCompare(b.occurrences[0]?.date || ''));
}

function MiniDayCalendar({ dayApts, habitTime, habitDuration, conflictAptIds = [], habitTitle }) {
  if (!habitTime) return null;
  const ROW_H = 20;
  const LABEL_W = 26;
  const habitMin = t2m(habitTime);
  const habitEndMin = habitMin + Math.max(1, habitDuration || 15);
  const aptMins = dayApts.flatMap(a => [t2m(a.startTime), t2m(a.endTime)]);
  const allMins = [habitMin, habitEndMin, ...aptMins].filter(n => !isNaN(n));
  const startHour = Math.max(4, Math.floor(Math.min(...allMins) / 60) - 1);
  const endHour = Math.min(23, Math.ceil(Math.max(...allMins) / 60) + 1);
  const m2px = (m) => ((m - startHour * 60) / 60) * ROW_H;
  const totalH = Math.max(80, (endHour - startHour) * ROW_H);
  const hours = [];
  for (let h = startHour; h <= endHour; h++) hours.push(h);
  const stillConflicts = dayApts.some(a => {
    const as2 = t2m(a.startTime), ae2 = t2m(a.endTime);
    return habitMin < ae2 && as2 < habitEndMin;
  });

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-[10px]">
        <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-red-300 border border-red-400"/><span className="text-slate-400">Termin</span></div>
        <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm border-2 border-dashed border-blue-500" style={{background:'repeating-linear-gradient(45deg,rgba(59,130,246,0.28) 0px,rgba(59,130,246,0.28) 3px,rgba(255,255,255,0.1) 3px,rgba(255,255,255,0.1) 7px)'}}/><span className="text-slate-400">Routine</span></div>
        <span className={`ml-auto font-medium ${stillConflicts ? 'text-red-600' : 'text-emerald-600'}`}>
          {stillConflicts ? '⚠ Überschneidung' : '✓ Kein Konflikt'}
        </span>
      </div>
      <div className="relative rounded-lg border border-slate-200 bg-white overflow-hidden" style={{height: totalH + 'px'}}>
        {hours.map(h => (
          <div key={h} className="absolute left-0 right-0 flex" style={{top: m2px(h * 60) + 'px'}}>
            <span className="text-[9px] text-slate-400 leading-none flex-shrink-0 pt-0.5 pr-0.5" style={{width: LABEL_W}}>{String(h).padStart(2,'0')}</span>
            <div className="flex-1 border-t border-slate-100"/>
          </div>
        ))}
        <div className="absolute" style={{left: LABEL_W + 2, right: 2, top: 0, bottom: 0}}>
          {/* Routine-Block (transparent, dahinter) */}
          {(() => {
            const top = m2px(habitMin);
            const height = Math.max(14, m2px(habitEndMin) - top);
            return (
              <div className="absolute left-0 right-0 rounded px-1 text-[9px] font-semibold flex items-center border-2 border-dashed border-blue-500 text-blue-900"
                style={{top, height, zIndex: 20, background: 'repeating-linear-gradient(45deg,rgba(59,130,246,0.28) 0px,rgba(59,130,246,0.28) 4px,rgba(255,255,255,0.08) 4px,rgba(255,255,255,0.08) 9px)'}}>
                {habitTitle ? habitTitle : 'Routine'} {m2t(habitMin)}–{m2t(habitEndMin)}
              </div>
            );
          })()}
          {/* Termine (darüber) */}
          {dayApts.map(apt => {
            const top = m2px(t2m(apt.startTime));
            const height = Math.max(14, m2px(t2m(apt.endTime)) - top);
            const isConflict = conflictAptIds.includes(apt.id);
            return (
              <div key={apt.id}
                className={`absolute left-0 right-0 rounded px-1 text-[9px] overflow-hidden flex items-center ${isConflict ? 'bg-red-200 border border-red-400 text-red-800 font-semibold' : 'bg-slate-100 text-slate-500'}`}
                style={{top, height, zIndex: isConflict ? 10 : 5}}>
                {apt.title} <span className="ml-0.5 opacity-60">{apt.startTime}–{apt.endTime}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function HabitCalendarConflictModal({
  isOpen, onClose, onDone,
  frequency, habitTime: initTime, habitDuration: initDuration,
  daysOfWeek, schichtZeiten: initSchichtZeiten,
  habitTitle, habitId, schichtRhythmus: initSchichtRhythmus,
}) {
  const [phase, setPhase] = useState('summary');
  const [groups, setGroups] = useState([]);
  const [allApts, setAllApts] = useState([]);
  const [groupIdx, setGroupIdx] = useState(0);
  const [skippedDates, setSkippedDates] = useState([]);
  const [dailyTimeOverrides, setDailyTimeOverrides] = useState({});
  const [activeAction, setActiveAction] = useState(null);
  const [globalTimeInput, setGlobalTimeInput] = useState(initTime || '09:00');
  const [globalDurInput, setGlobalDurInput] = useState(initDuration || 15);
  const [dayTimeInput, setDayTimeInput] = useState('');
  const [newAptTime, setNewAptTime] = useState('');
  const [newAptDur, setNewAptDur] = useState(30);
  // Pending appointment changes – nur beim finalen Speichern in localStorage schreiben
  const pendingAptRef = useRef({ updates: {}, deletes: new Set() });

  const loadWith = useCallback((time, dur) => {
    const apts = JSON.parse(userStorage.getItem('zielplanung_appointments') || '[]');
    setAllApts(apts);
    // Beim initialen Laden noch keine skippedDates/dailyTimeOverrides vorhanden
    const g = collectConflictGroups(frequency, daysOfWeek, initSchichtZeiten, time, dur, apts, [], {}, habitId ?? null, initSchichtRhythmus ?? null);
    setGroups(g);
    return g;
  }, [frequency, daysOfWeek, initSchichtZeiten, habitId]);

  useEffect(() => {
    if (!isOpen) return;
    setPhase('summary');
    setGroupIdx(0);
    setSkippedDates([]);
    setDailyTimeOverrides({});
    setActiveAction(null);
    setGlobalTimeInput(initTime || '09:00');
    setGlobalDurInput(initDuration || 15);
    setDayTimeInput('');
    pendingAptRef.current = { updates: {}, deletes: new Set() };
    loadWith(initTime, initDuration);
  }, [isOpen]); // eslint-disable-line

  const totalConflicts = groups.reduce((s, g) => s + g.occurrences.length, 0);
  const currentGroup = groups[groupIdx];
  const firstOcc = currentGroup?.occurrences[0];

  const previewDayApts = useMemo(() => {
    if (!firstOcc || !allApts) return [];
    const base = allApts.filter(a => a.date === firstOcc.date && a.startTime && a.endTime);
    if (activeAction !== 'aptTime' || !newAptTime) return base;
    const newEnd = m2t(t2m(newAptTime) + Math.max(5, newAptDur));
    return base.map(a => a.id === firstOcc.id ? { ...a, startTime: newAptTime, endTime: newEnd } : a);
  }, [firstOcc, allApts, activeAction, newAptTime, newAptDur]);

  const previewTime = activeAction === 'dayTime' && dayTimeInput
    ? dayTimeInput
    : currentGroup?.habitTime || globalTimeInput || initTime || '09:00';
  const previewDuration = currentGroup?.habitDuration || globalDurInput || initDuration || 15;

  // Prüft ob neue Routine-Zeit mit anderen Terminen an IRGENDEINEM Tag der Gruppe kollidiert
  const dayTimeConflict = useMemo(() => {
    if (!dayTimeInput || !firstOcc || !currentGroup) return null;
    const hs = t2m(dayTimeInput);
    const he = hs + (currentGroup?.habitDuration || initDuration || 15);
    for (const occ of currentGroup.occurrences) {
      const dayApts = allApts.filter(a => a.date === occ.date && a.startTime && a.endTime);
      for (const apt of dayApts) {
        if (hs < t2m(apt.endTime) && t2m(apt.startTime) < he)
          return `Konflikt am ${fmtDate(occ.date)} mit „${apt.title || 'Termin'}"`;
      }
    }
    return null;
  }, [dayTimeInput, firstOcc, allApts, currentGroup, initDuration]);

  // Prüft ob verschobener Termin mit Routine oder anderen Terminen kollidiert
  const aptMoveConflict = useMemo(() => {
    if (!newAptTime || !firstOcc) return null;
    const as = t2m(newAptTime), ae = as + Math.max(5, newAptDur);
    const hs = t2m(currentGroup?.habitTime || initTime || '09:00');
    const he = hs + (currentGroup?.habitDuration || initDuration || 15);
    if (as < he && hs < ae) return `Überschneidung mit der Routine (${m2t(hs)}–${m2t(he)})`;
    const otherApts = allApts.filter(a => a.date === firstOcc.date && a.startTime && a.endTime && a.id !== firstOcc.id);
    for (const apt of otherApts) {
      if (as < t2m(apt.endTime) && t2m(apt.startTime) < ae) return `Konflikt mit „${apt.title || 'Termin'}"`;
    }
    return null;
  }, [newAptTime, newAptDur, firstOcc, allApts, currentGroup, initTime, initDuration]);

  const handleRecheck = () => {
    // Neu prüfen mit aktuellem allApts UND bereits gesetzten skippedDates/dailyTimeOverrides
    const newGroups = collectConflictGroups(frequency, daysOfWeek, initSchichtZeiten, globalTimeInput, globalDurInput, allApts, skippedDates, dailyTimeOverrides, habitId ?? null, initSchichtRhythmus ?? null);
    setGroups(newGroups);
    if (newGroups.length === 0) {
      onDone({
        globalTime: globalTimeInput, globalDuration: globalDurInput,
        skippedDates, dailyTimeOverrides,
        aptUpdates: { ...pendingAptRef.current.updates },
        aptDeletes: [...pendingAptRef.current.deletes],
      });
    }
  };

  // Löst die GESAMTE aktuelle Gruppe auf einmal auf (für alle Aktionen)
  const resolveCurrentGroup = useCallback((newSkipped, newOverrides) => {
    const newGroups = [...groups];
    newGroups.splice(groupIdx, 1);
    const newIdx = groupIdx >= newGroups.length ? 0 : groupIdx;
    const finalSkipped = newSkipped ? [...skippedDates, ...newSkipped] : skippedDates;
    const finalOverrides = newOverrides ? { ...dailyTimeOverrides, ...newOverrides } : dailyTimeOverrides;
    setGroups(newGroups);
    setSkippedDates(finalSkipped);
    setDailyTimeOverrides(finalOverrides);
    setActiveAction(null);
    setDayTimeInput('');
    if (newGroups.length === 0) {
      onDone({
        globalTime: globalTimeInput, globalDuration: globalDurInput,
        skippedDates: finalSkipped, dailyTimeOverrides: finalOverrides,
        aptUpdates: { ...pendingAptRef.current.updates },
        aptDeletes: [...pendingAptRef.current.deletes],
      });
    } else {
      setGroupIdx(newIdx);
    }
  }, [groups, groupIdx, skippedDates, dailyTimeOverrides, globalTimeInput, globalDurInput, onDone]);

  const handleDayTimeApply = () => {
    if (!firstOcc || !dayTimeInput || !currentGroup) return;
    // Zeit für ALLE Vorkommen der Gruppe übernehmen
    const overrides = {};
    for (const occ of currentGroup.occurrences) {
      overrides[occ.date] = dayTimeInput;
    }
    resolveCurrentGroup(null, overrides);
  };

  const handleSkipHabitDay = () => {
    if (!firstOcc || !currentGroup) return;
    // ALLE Vorkommen der Gruppe überspringen
    const allDates = currentGroup.occurrences.map(o => o.date);
    resolveCurrentGroup(allDates, null);
  };

  const handleMoveApt = () => {
    if (!newAptTime || !firstOcc) return;
    const newEnd = m2t(t2m(newAptTime) + Math.max(5, newAptDur));
    // ALLE Vorkommen der Gruppe gleichzeitig verschieben
    const groupOccs = currentGroup?.occurrences || [firstOcc];
    for (const occ of groupOccs) {
      pendingAptRef.current.updates[occ.id != null ? occ.id : `${occ.date}||${occ.startTime}`] = { ...occ, startTime: newAptTime, endTime: newEnd };
    }
    // Lokalen State aktualisieren
    const updated = allApts.map(a => {
      const match = groupOccs.find(o => (o.id != null ? o.id : `${o.date}||${o.startTime}`) === (a.id != null ? a.id : `${a.date}||${a.startTime}`));
      return match ? { ...a, startTime: newAptTime, endTime: newEnd } : a;
    });
    setAllApts(updated);
    // Gruppen NEU berechnen aus den aktualisierten Terminen + bereits gesetzten skippedDates/dailyTimeOverrides
    const recomputed = collectConflictGroups(frequency, daysOfWeek, initSchichtZeiten, globalTimeInput, globalDurInput, updated, skippedDates, dailyTimeOverrides, habitId ?? null, initSchichtRhythmus ?? null);
    setGroups(recomputed);
    setGroupIdx(0);
    setActiveAction(null);
    setDayTimeInput('');
    if (recomputed.length === 0) {
      onDone({
        globalTime: globalTimeInput, globalDuration: globalDurInput,
        skippedDates, dailyTimeOverrides,
        aptUpdates: { ...pendingAptRef.current.updates },
        aptDeletes: [...pendingAptRef.current.deletes],
      });
    }
  };

  const handleDeleteApt = () => {
    if (!firstOcc) return;
    // ALLE Vorkommen der Gruppe gleichzeitig löschen
    const groupOccs = currentGroup?.occurrences || [firstOcc];
    for (const occ of groupOccs) {
      pendingAptRef.current.deletes.add(occ.id != null ? occ.id : `${occ.date}||${occ.startTime}`);
    }
    // Lokalen State aktualisieren
    const ids = new Set(groupOccs.map(o => o.id != null ? o.id : `${o.date}||${o.startTime}`));
    const updated = allApts.filter(a => !ids.has(a.id != null ? a.id : `${a.date}||${a.startTime}`));
    setAllApts(updated);
    // Gruppen NEU berechnen aus den aktualisierten Terminen + bereits gesetzten skippedDates/dailyTimeOverrides
    const recomputed = collectConflictGroups(frequency, daysOfWeek, initSchichtZeiten, globalTimeInput, globalDurInput, updated, skippedDates, dailyTimeOverrides, habitId ?? null, initSchichtRhythmus ?? null);
    setGroups(recomputed);
    setGroupIdx(0);
    setActiveAction(null);
    setDayTimeInput('');
    if (recomputed.length === 0) {
      onDone({
        globalTime: globalTimeInput, globalDuration: globalDurInput,
        skippedDates, dailyTimeOverrides,
        aptUpdates: { ...pendingAptRef.current.updates },
        aptDeletes: [...pendingAptRef.current.deletes],
      });
    }
  };

  const handleNext = () => {
    if (groups.length <= 1) return;
    setGroupIdx(i => (i + 1) % groups.length);
    setActiveAction(null);
    setDayTimeInput('');
  };

  if (!isOpen) return null;

  const shownGroups = groups.slice(0, MAX_SHOWN);
  const hiddenGroupCount = Math.max(0, groups.length - MAX_SHOWN);

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}/>
      <div className="relative bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500"/>
            <span className="font-semibold text-slate-800 text-sm">
              {phase === 'summary'
                ? `${totalConflicts} Konflikte · ${groups.length} Gruppe${groups.length !== 1 ? 'n' : ''}`
                : `Konflikt ${groupIdx + 1} von ${groups.length}`}
            </span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100" data-testid="conflict-modal-close">
            <X className="w-4 h-4 text-slate-500"/>
          </button>
        </div>

        <div className="p-4 space-y-3">

          {phase === 'summary' && (
            <>
              <div className="space-y-2">
                {shownGroups.map((g) => (
                  <div key={g.key} className="flex items-center gap-2 p-2.5 bg-red-50 rounded-xl border border-red-100">
                    <div className="w-6 h-6 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                      {g.occurrences.length > MAX_SHOWN ? `${MAX_SHOWN}+` : g.occurrences.length}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800 truncate">
                        {g.shiftType && <span className="text-purple-600 font-semibold">[{g.shiftType}] </span>}
                        {g.title}
                      </p>
                      <p className="text-[10px] text-slate-500">
                        {g.origStartTime}–{g.origEndTime} · {g.occurrences.length}× · Nächster: {fmtDate(g.occurrences[0].date)}
                      </p>
                    </div>
                  </div>
                ))}
                {hiddenGroupCount > 0 && (
                  <p className="text-[11px] text-slate-500 text-center py-1 font-medium">
                    + {hiddenGroupCount} weitere Gruppe{hiddenGroupCount !== 1 ? 'n' : ''}
                  </p>
                )}
              </div>

              {frequency !== 'schichten' && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                  <p className="text-[11px] font-semibold text-slate-600">Routine-Zeit anpassen & neu prüfen</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input type="time" value={globalTimeInput} onChange={e => setGlobalTimeInput(e.target.value)}
                      className="px-2 py-1 border border-slate-200 rounded-lg text-xs bg-white" data-testid="global-time-input"/>
                    <input type="number" min={5} max={240} step={5} value={globalDurInput}
                      onChange={e => setGlobalDurInput(Number(e.target.value))}
                      className="w-14 px-2 py-1 border border-slate-200 rounded-lg text-xs text-center bg-white"/>
                    <span className="text-[10px] text-slate-400">Min.</span>
                    <button onClick={handleRecheck}
                      className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-900 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
                      data-testid="recheck-btn">
                      <RefreshCw className="w-3 h-3"/>
                      Neu prüfen
                    </button>
                  </div>
                  {(globalTimeInput !== (initTime || '09:00') || globalDurInput !== (initDuration || 15)) && (
                    <p className="text-[10px] text-blue-600">
                      Geändert: {initTime}, {initDuration} Min. → {globalTimeInput}, {globalDurInput} Min.
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button onClick={onClose} className="flex-1 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50" data-testid="conflict-cancel">
                  Abbrechen
                </button>
                <button onClick={() => { setGroupIdx(0); setPhase('resolving'); setActiveAction(null); }}
                  className="flex-1 py-2 bg-emerald-600 text-white text-sm rounded-xl hover:bg-emerald-700 flex items-center justify-center gap-1 font-medium" data-testid="conflict-solve">
                  Konflikte lösen <ChevronRight className="w-4 h-4"/>
                </button>
              </div>
            </>
          )}

          {phase === 'resolving' && currentGroup && firstOcc && (
            <>
              <div>
                <p className="text-xs font-semibold text-slate-800">
                  {currentGroup.shiftType && <span className="text-purple-600">[{currentGroup.shiftType}] </span>}
                  {currentGroup.title}
                  {currentGroup.occurrences.length > 1 && (
                    <span className="text-slate-400 font-normal ml-1">({currentGroup.occurrences.length}× betroffen)</span>
                  )}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  Nächster: <strong>{fmtDate(firstOcc.date)}</strong> · {firstOcc.startTime}–{firstOcc.endTime}
                </p>
                {currentGroup.occurrences.length > 1 && (
                  <p className="text-[10px] text-slate-400">
                    Weitere: {currentGroup.occurrences.slice(1, MAX_SHOWN).map(o => fmtDate(o.date)).join(', ')}
                    {currentGroup.occurrences.length > MAX_SHOWN && ` +${currentGroup.occurrences.length - MAX_SHOWN}`}
                  </p>
                )}
              </div>

              <div className="max-h-36 overflow-y-auto rounded-lg">
                <MiniDayCalendar
                  dayApts={previewDayApts}
                  habitTime={previewTime}
                  habitDuration={previewDuration}
                  conflictAptIds={[firstOcc.id]}
                  habitTitle={habitTitle}
                />
              </div>

              <div className="space-y-2">
                {/* Aktion 1: Zeit der Routine anpassen (für alle Tage der Gruppe) */}
                <div className="border border-blue-200 rounded-xl overflow-hidden">
                  <button className="w-full flex items-center gap-2 p-3 bg-blue-50 text-left"
                    onClick={() => {
                      setDayTimeInput(currentGroup.habitTime || globalTimeInput || initTime || '09:00');
                      setActiveAction(activeAction === 'dayTime' ? null : 'dayTime');
                    }}
                    data-testid="action-habit-time">
                    <Clock className="w-4 h-4 text-blue-500 flex-shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-blue-700">Zeit der Routine anpassen</p>
                      <p className="text-[10px] text-blue-400">
                        {currentGroup.occurrences.length > 1
                          ? `Für alle ${currentGroup.occurrences.length} Tage dieser Gruppe`
                          : `Nur für ${fmtDate(firstOcc.date)}`}
                      </p>
                    </div>
                    <ChevronRight className={`w-3 h-3 text-blue-300 flex-shrink-0 transition-transform ${activeAction === 'dayTime' ? 'rotate-90' : ''}`}/>
                  </button>
                  {activeAction === 'dayTime' && (
                    <div className="p-3 border-t border-blue-100 bg-white space-y-2">
                      <div className="flex items-center gap-2">
                        <input type="time" value={dayTimeInput} onChange={e => setDayTimeInput(e.target.value)}
                          className="px-2 py-1 border border-slate-200 rounded-lg text-xs" data-testid="habit-time-input"/>
                        <span className="text-[10px] text-slate-400">
                          {currentGroup.occurrences.length > 1 ? `Für alle ${currentGroup.occurrences.length} Tage` : 'Nur für diesen Tag'}
                        </span>
                      </div>
                      {dayTimeInput && (
                        <p className={`text-[10px] font-semibold ${dayTimeConflict ? 'text-red-600' : 'text-emerald-600'}`}>
                          {dayTimeConflict ? `✗ ${dayTimeConflict}` : '✓ Kein Konflikt – Zeit passt!'}
                        </p>
                      )}
                      <button onClick={handleDayTimeApply}
                        disabled={!dayTimeInput || !!dayTimeConflict}
                        className={`w-full py-1.5 text-xs rounded-lg font-medium transition-colors ${(!dayTimeInput || dayTimeConflict) ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                        data-testid="apply-day-time-btn">
                        {currentGroup.occurrences.length > 1 ? `Für alle ${currentGroup.occurrences.length} Tage anpassen` : 'Für diesen Tag anpassen'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Aktion 2: Termin verschieben (alle Vorkommen) */}
                <div className="border border-amber-200 rounded-xl overflow-hidden">
                  <button className="w-full flex items-center gap-2 p-3 bg-amber-50 text-left"
                    onClick={() => {
                      setNewAptTime(firstOcc.startTime || '09:00');
                      setNewAptDur(Math.max(15, t2m(firstOcc.endTime) - t2m(firstOcc.startTime)));
                      setActiveAction(activeAction === 'aptTime' ? null : 'aptTime');
                    }}
                    data-testid="action-apt-time">
                    <ArrowRight className="w-4 h-4 text-amber-500 flex-shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-amber-700">Termin verschieben</p>
                      <p className="text-[10px] text-amber-400">
                        {firstOcc.startTime}–{firstOcc.endTime}
                        {currentGroup.occurrences.length > 1 ? ` · alle ${currentGroup.occurrences.length} Termine` : ` · ${fmtDate(firstOcc.date)}`}
                      </p>
                    </div>
                    <ChevronRight className={`w-3 h-3 text-amber-300 flex-shrink-0 transition-transform ${activeAction === 'aptTime' ? 'rotate-90' : ''}`}/>
                  </button>
                  {activeAction === 'aptTime' && (
                    <div className="p-3 border-t border-amber-100 bg-white space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-500 w-10">Zeit</span>
                        <input type="time" value={newAptTime} onChange={e => setNewAptTime(e.target.value)}
                          className="px-2 py-1 border border-slate-200 rounded-lg text-xs" data-testid="apt-time-input"/>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-500 w-10">Dauer</span>
                        <input type="number" min={5} max={480} step={5} value={newAptDur}
                          onChange={e => setNewAptDur(Number(e.target.value))}
                          className="w-14 px-2 py-1 border border-slate-200 rounded-lg text-xs text-center"/>
                        <span className="text-[10px] text-slate-400">Min.</span>
                      </div>
                      {newAptTime && (
                        <p className={`text-[10px] font-semibold ${aptMoveConflict ? 'text-red-600' : 'text-emerald-600'}`}>
                          {aptMoveConflict ? `✗ ${aptMoveConflict}` : '✓ Kein Konflikt – Zeit passt!'}
                        </p>
                      )}
                      <button onClick={handleMoveApt}
                        disabled={!newAptTime || !!aptMoveConflict}
                        className={`w-full py-1.5 text-xs rounded-lg font-medium transition-colors ${(!newAptTime || aptMoveConflict) ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-amber-600 text-white hover:bg-amber-700'}`}
                        data-testid="move-apt-btn">
                        {currentGroup.occurrences.length > 1 ? `Alle ${currentGroup.occurrences.length} Termine verschieben` : 'Termin verschieben'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Aktion 3: Termin löschen (alle Vorkommen) */}
                <div className="border border-red-200 rounded-xl overflow-hidden">
                  <button className="w-full flex items-center gap-2 p-3 bg-red-50 text-left"
                    onClick={() => setActiveAction(activeAction === 'deleteApt' ? null : 'deleteApt')}
                    data-testid="action-delete-apt">
                    <Trash2 className="w-4 h-4 text-red-500 flex-shrink-0"/>
                    <p className="text-xs font-medium text-red-700 flex-1 min-w-0 truncate">
                      {currentGroup.occurrences.length > 1
                        ? `Alle ${currentGroup.occurrences.length} Termine „${currentGroup.title}" löschen`
                        : `Termin „${firstOcc.title || currentGroup.title}" löschen`}
                    </p>
                    <ChevronRight className={`w-3 h-3 text-red-300 flex-shrink-0 transition-transform ${activeAction === 'deleteApt' ? 'rotate-90' : ''}`}/>
                  </button>
                  {activeAction === 'deleteApt' && (
                    <div className="p-3 border-t border-red-100 bg-white space-y-2">
                      <p className="text-[11px] text-slate-700">
                        {currentGroup.occurrences.length > 1
                          ? <><strong>Alle {currentGroup.occurrences.length} Termine</strong> „{currentGroup.title}" endgültig aus dem Kalender löschen?</>
                          : <>Termin <strong>„{firstOcc.title || currentGroup.title}"</strong> endgültig aus dem Kalender löschen?</>}
                      </p>
                      <button onClick={handleDeleteApt}
                        className="w-full py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 font-medium" data-testid="confirm-delete-apt">
                        Endgültig löschen
                      </button>
                    </div>
                  )}
                </div>

                {/* Aktion 4: Routine überspringen (alle Tage der Gruppe) */}
                <div className="border border-orange-200 rounded-xl overflow-hidden">
                  <button onClick={() => setActiveAction(activeAction === 'skip' ? null : 'skip')}
                    className="w-full flex items-center gap-2 p-3 bg-orange-50 text-left"
                    data-testid="action-skip-habit">
                    <Calendar className="w-4 h-4 text-orange-400 flex-shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-orange-700">Routine überspringen</p>
                      <p className="text-[10px] text-orange-400">
                        {currentGroup.occurrences.length > 1
                          ? `An allen ${currentGroup.occurrences.length} Tagen dieser Gruppe`
                          : `Nur für ${fmtDate(firstOcc.date)}`}
                      </p>
                    </div>
                    <ChevronRight className={`w-3 h-3 text-orange-300 flex-shrink-0 transition-transform ${activeAction === 'skip' ? 'rotate-90' : ''}`}/>
                  </button>
                  {activeAction === 'skip' && (
                    <div className="p-3 border-t border-orange-100 bg-white space-y-2">
                      <div className="flex items-start gap-1.5 p-2 bg-amber-50 rounded-lg border border-amber-200">
                        <Info className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5"/>
                        <p className="text-[10px] text-amber-700">
                          <strong>Empfehlung:</strong> Verschiebe lieber den Termin, um deine Routine aufrechtzuerhalten!
                        </p>
                      </div>
                      <button onClick={handleSkipHabitDay}
                        className="w-full py-1.5 bg-orange-500 text-white text-xs rounded-lg hover:bg-orange-600 font-medium" data-testid="confirm-skip">
                        {currentGroup.occurrences.length > 1 ? `Alle ${currentGroup.occurrences.length} Tage überspringen` : 'Trotzdem überspringen'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {groups.length > 1 && (
                <button onClick={handleNext}
                  className="w-full py-2 border border-slate-200 text-slate-500 text-sm rounded-xl hover:bg-slate-50 flex items-center justify-center gap-1 transition-colors"
                  data-testid="next-conflict-btn">
                  <span>Nächster Konflikt</span>
                  <ChevronRight className="w-4 h-4"/>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
